/**
 * OneTrust consent for A/B testing. The A/B category decides whether an exposure is sent (AbExposure) and whether a
 * visitor who declined is moved to `control` (persistBucket.ts). Fastly gives every visitor a bucket before they
 * answer the banner, so having an `ab_bucket` cookie says nothing about consent.
 */
import { env } from '@zopauk/nextjs-env';

declare global {
  interface Window {
    // OneTrust: comma-separated consented categories, e.g. ',C0001,C0002,' (undefined until OneTrust loads)
    OneTrust?: {
      IsAlertBoxClosed: () => boolean;
    };
    OnetrustActiveGroups?: string;
  }
}

// Provisional default: the OneTrust category A/B testing belongs to. Confirm with the cookie-policy owner
// (the cookies standard lists C0002 as Performance). Overridable with NEXT_PUBLIC_ONETRUST_AB_GROUP, so it can
// follow the OneTrust config without a code change (same pattern as signup-web's ONETRUST_TARGETING_GROUP).
export const DEFAULT_AB_CONSENT_CATEGORY = 'C0002';

export const getAbConsentCategory = (): string => env('NEXT_PUBLIC_ONETRUST_AB_GROUP') || DEFAULT_AB_CONSENT_CATEGORY;

// OneTrust dispatches this when it loads and whenever the visitor changes their consent.
export const CONSENT_EVENT = 'OneTrustGroupsUpdated';

// `undefined` = OneTrust hasn't loaded yet; its CONSENT_EVENT follows once it has.
export function hasAbConsent(): undefined | boolean {
  const groups = window.OnetrustActiveGroups;
  return groups === undefined ? undefined : groups.split(',').includes(getAbConsentCategory());
}

export const hasAnsweredConsent = (): boolean => window.OneTrust?.IsAlertBoxClosed() ?? false;
